import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { v: "self_paced", l: "Self-paced" }, { v: "live", l: "Live" }, { v: "blended", l: "Blended" },
  { v: "certification", l: "Certification" }, { v: "compliance", l: "Compliance" },
];
const LEVELS = ["beginner", "intermediate", "advanced"];
const STANDARDS = ["NABH", "NABL", "ISO", "JCI", "INTERNAL_AUDIT"];

const EMPTY = {
  course_title: "", course_code: "", category_id: "",
  short_description: "", detailed_description: "",
  thumbnail: "", banner: "",
  course_type: "self_paced", course_level: "beginner",
  language: "English", estimated_duration_hours: 4, course_validity_days: 365, certificate_validity_days: 730,
  course_objective: "", learning_outcomes: "", target_audience: "", prerequisites: "",
  trainer_user_id: "", trainer_name: "", trainer_designation: "Course Trainer",
  trainer_signature_path: "", applicable_standards: [],
  certificate_applicable: true, is_free: true, base_price: 0, discount_price: 0, gst_applicable: false,
  status: "draft", featured: false, mandatory_eligible: false, version_notes: "",
  // Storefront / marketing fields — control the public course landing page.
  preview_video_url: "", brochure_url: "", enrolled_count_placeholder: 0, rating_placeholder: 4.7, course_badge: "",
  // GST details
  gst_percentage: 18, tax_inclusive: false,
};

export default function AdminCourseFormPage({ mode = "create" }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const isEdit = mode === "edit";

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/course-categories"); setCats(data.data.categories); }
      catch { /* silent */ }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/courses/${courseId}`);
      const c = data.data.course;
      setForm({
        ...EMPTY,
        ...c,
        thumbnail: c.thumbnail || "", banner: c.banner || "",
        short_description: c.short_description || "", detailed_description: c.detailed_description || "",
        course_objective: c.course_objective || "", learning_outcomes: c.learning_outcomes || "",
        target_audience: c.target_audience || "", prerequisites: c.prerequisites || "",
        trainer_user_id: c.trainer_user_id || "",
        trainer_name: c.trainer_name || "",
        trainer_designation: c.trainer_designation || "Course Trainer",
        trainer_signature_path: c.trainer_signature_path || "",
        version_notes: c.version_notes || "",
        applicable_standards: c.applicable_standards || [],
        preview_video_url: c.preview_video_url || "",
        brochure_url: c.brochure_url || "",
        gst_percentage: c.gst_percentage ?? 18,
        tax_inclusive: !!c.tax_inclusive,
        enrolled_count_placeholder: c.enrolled_count_placeholder ?? 0,
        rating_placeholder: c.rating_placeholder ?? 4.7,
        course_badge: c.course_badge || "",
      });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [courseId, isEdit]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleStandard = (s) => {
    setForm((f) => ({ ...f, applicable_standards: f.applicable_standards.includes(s)
      ? f.applicable_standards.filter((x) => x !== s)
      : [...f.applicable_standards, s] }));
  };

  const submit = async () => {
    if (!form.course_title || !form.course_code || !form.category_id) {
      toast.error("Title, code and category are required.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form };
      if (isEdit) {
        delete body.course_code;
        delete body.status;
        delete body.id;
        delete body.created_at; delete body.updated_at; delete body.created_by; delete body.updated_by;
        delete body.deleted_at; delete body.current_version;
        await api.put(`/admin/courses/${courseId}`, body);
        toast.success("Course updated.");
        navigate(`/app/super-admin/courses/${courseId}`);
      } else {
        const { data } = await api.post("/admin/courses", body);
        toast.success("Course created.");
        navigate(`/app/super-admin/courses/${data.data.course.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout title={isEdit ? "Edit course" : "New course"} breadcrumbs={["Platform", "Courses", isEdit ? "Edit" : "New"]}>
      <div className="mx-auto max-w-4xl space-y-6" data-testid="admin-course-form">
        <Link to={isEdit ? `/app/super-admin/courses/${courseId}` : "/app/super-admin/courses"} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Link>

        {loading ? <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Loading…</div> : (
          <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6">
            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Basic details</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2"><Label>Course title *</Label><Input value={form.course_title} onChange={(e) => set("course_title", e.target.value)} data-testid="course-form-title" /></div>
                <div className="space-y-1"><Label>Course code *</Label><Input value={form.course_code} disabled={isEdit} onChange={(e) => set("course_code", e.target.value.toUpperCase())} placeholder="e.g. IPC-101" data-testid="course-form-code" /></div>
                <div className="space-y-1">
                  <Label>Category *</Label>
                  <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                    <SelectTrigger data-testid="course-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.category_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 sm:col-span-2"><Label>Short description</Label><Textarea rows={2} value={form.short_description} onChange={(e) => set("short_description", e.target.value)} /></div>
                <div className="space-y-1 sm:col-span-2"><Label>Detailed description</Label><Textarea rows={4} value={form.detailed_description} onChange={(e) => set("detailed_description", e.target.value)} /></div>
                <div className="space-y-1 sm:col-span-2">
                  <Label>Thumbnail</Label>
                  <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                    <div className="text-[10px] text-slate-500 leading-relaxed">
                      Guidelines — <b>Auto-resized to 400×300 (4:3)</b>. Upload any large image — we automatically crop-fit and optimize it. Formats: <b>JPG / PNG / WebP</b>. Max size <b>5 MB</b>.
                    </div>
                    {form.thumbnail && (
                      <div className="flex items-center gap-3">
                        <img src={resolveImg(form.thumbnail)} alt="Thumbnail preview" className="h-16 w-24 object-cover rounded border border-slate-200" data-testid="course-thumb-preview" />
                        <button type="button" onClick={() => set("thumbnail", "")} className="text-xs font-bold text-rose-600 hover:underline">Remove</button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {mode === "edit" && (
                        <label className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="course-thumb-upload-btn">
                          Upload image
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                            onChange={async (e) => {
                              const f = e.target.files?.[0]; if (!f) return;
                              if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB."); return; }
                              const fd = new FormData(); fd.append("file", f);
                              try {
                                const { data } = await api.post(`/admin/courses/${courseId}/image?image_kind=thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                                set("thumbnail", data.data.thumbnail);
                                toast.success("Thumbnail uploaded.");
                              } catch (err) { toast.error(formatApiError(err)); }
                            }} data-testid="course-thumb-file" />
                        </label>
                      )}
                      <Input value={form.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} placeholder="…or paste image URL" data-testid="course-form-thumbnail" />
                    </div>
                    {mode === "create" && (
                      <div className="text-[10px] text-amber-700">Save the course first, then upload an image here (file upload requires an existing course).</div>
                    )}
                  </div>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label>Banner</Label>
                  <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                    <div className="text-[10px] text-slate-500 leading-relaxed">
                      Guidelines — <b>Auto-resized to 1600×600 (wide banner)</b>. Upload any large image — we automatically crop-fit and optimize it. Formats: <b>JPG / PNG / WebP</b>. Max size <b>5 MB</b>.
                    </div>
                    {form.banner && (
                      <div className="flex items-center gap-3">
                        <img src={resolveImg(form.banner)} alt="Banner preview" className="h-16 w-40 object-cover rounded border border-slate-200" data-testid="course-banner-preview" />
                        <button type="button" onClick={() => set("banner", "")} className="text-xs font-bold text-rose-600 hover:underline">Remove</button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {mode === "edit" && (
                        <label className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="course-banner-upload-btn">
                          Upload image
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                            onChange={async (e) => {
                              const f = e.target.files?.[0]; if (!f) return;
                              if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB."); return; }
                              const fd = new FormData(); fd.append("file", f);
                              try {
                                const { data } = await api.post(`/admin/courses/${courseId}/image?image_kind=banner`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                                set("banner", data.data.banner);
                                toast.success("Banner uploaded.");
                              } catch (err) { toast.error(formatApiError(err)); }
                            }} data-testid="course-banner-file" />
                        </label>
                      )}
                      <Input value={form.banner} onChange={(e) => set("banner", e.target.value)} placeholder="…or paste image URL" data-testid="course-form-banner" />
                    </div>
                    {mode === "create" && (
                      <div className="text-[10px] text-amber-700">Save the course first, then upload an image here.</div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Delivery</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select value={form.course_type} onValueChange={(v) => set("course_type", v)}>
                    <SelectTrigger data-testid="course-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Level</Label>
                  <Select value={form.course_level} onValueChange={(v) => set("course_level", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Language</Label><Input value={form.language} onChange={(e) => set("language", e.target.value)} /></div>
                <div className="space-y-1"><Label>Duration (hours)</Label><Input type="number" value={form.estimated_duration_hours} onChange={(e) => set("estimated_duration_hours", parseFloat(e.target.value || "0"))} /></div>
                <div className="space-y-1"><Label>Course validity (days)</Label><Input type="number" value={form.course_validity_days} onChange={(e) => set("course_validity_days", parseInt(e.target.value || "0", 10))} /></div>
                <div className="space-y-1"><Label>Certificate validity (days)</Label><Input type="number" value={form.certificate_validity_days || 0} onChange={(e) => set("certificate_validity_days", parseInt(e.target.value || "0", 10))} /></div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Healthcare details</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <Label>Applicable standards</Label>
                  <div className="flex flex-wrap gap-2">
                    {STANDARDS.map((s) => (
                      <button key={s} type="button" onClick={() => toggleStandard(s)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.applicable_standards.includes(s) ? "border-[#329112] bg-[#329112]/10 text-[#329112]" : "border-slate-200 bg-white text-slate-600"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1 sm:col-span-2"><Label>Course objective</Label><Textarea rows={2} value={form.course_objective} onChange={(e) => set("course_objective", e.target.value)} /></div>
                <div className="space-y-1 sm:col-span-2"><Label>Learning outcomes</Label><Textarea rows={2} value={form.learning_outcomes} onChange={(e) => set("learning_outcomes", e.target.value)} /></div>
                <div className="space-y-1"><Label>Target audience</Label><Textarea rows={2} value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} /></div>
                <div className="space-y-1"><Label>Prerequisites</Label><Textarea rows={2} value={form.prerequisites} onChange={(e) => set("prerequisites", e.target.value)} /></div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Storefront &amp; marketing</h3>
              <p className="mt-1 text-xs text-slate-500">
                Controls what appears on the <b>public course landing page</b> — the &ldquo;About this course&rdquo;
                text (uses your <i>Detailed description</i> above), a YouTube preview video, and dummy
                stat badges (learners enrolled, rating) that display until real data is available.
              </p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1 sm:col-span-2">
                  <Label>Preview YouTube video URL</Label>
                  <Input value={form.preview_video_url}
                         onChange={(e) => set("preview_video_url", e.target.value)}
                         placeholder="https://www.youtube.com/watch?v=..."
                         data-testid="course-form-preview-video" />
                  {form.preview_video_url && !/^https?:\/\/.+/i.test(form.preview_video_url.trim()) && (
                    <div className="text-[11px] font-semibold text-red-600">Please paste a full URL starting with https:// (e.g. https://youtu.be/xxxx). Plain text will be ignored on the storefront.</div>
                  )}
                  <div className="text-[11px] text-slate-500">Paste any YouTube share URL. It will appear as an embedded &ldquo;Preview this course&rdquo; player on the landing page.</div>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label>Course brochure (PDF)</Label>
                  <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                    <div className="text-[10px] text-slate-500 leading-relaxed">
                      Upload a course brochure — students will see a <b>Download Brochure</b> button on the storefront landing page. Format: <b>PDF only</b>. Max size <b>15 MB</b>.
                    </div>
                    {form.brochure_url && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <a href={resolveImg(form.brochure_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-[#101876] hover:bg-slate-50" data-testid="course-brochure-preview">
                          View current brochure
                        </a>
                        <button type="button" onClick={async () => {
                          try {
                            await api.delete(`/admin/courses/${courseId}/brochure`);
                            set("brochure_url", "");
                            toast.success("Brochure removed.");
                          } catch (err) { toast.error(formatApiError(err)); }
                        }} className="text-xs font-bold text-rose-600 hover:underline" data-testid="course-brochure-remove">
                          Remove
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {mode === "edit" ? (
                        <label className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="course-brochure-upload-btn">
                          {form.brochure_url ? "Replace brochure" : "Upload brochure"}
                          <input type="file" accept="application/pdf" className="hidden"
                            onChange={async (e) => {
                              const f = e.target.files?.[0]; if (!f) return;
                              if (f.size > 15 * 1024 * 1024) { toast.error("Max 15 MB."); return; }
                              const fd = new FormData(); fd.append("file", f);
                              try {
                                const { data } = await api.post(`/admin/courses/${courseId}/brochure`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                                set("brochure_url", data.data.brochure_url);
                                toast.success("Brochure uploaded.");
                              } catch (err) { toast.error(formatApiError(err)); }
                            }} data-testid="course-brochure-file" />
                        </label>
                      ) : (
                        <div className="text-[10px] text-amber-700">Save the course first, then upload a brochure here.</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Learners enrolled (display)</Label>
                  <Input type="number" min="0" value={form.enrolled_count_placeholder}
                         onChange={(e) => set("enrolled_count_placeholder", parseInt(e.target.value || "0", 10))}
                         data-testid="course-form-enrolled-placeholder" />
                  <div className="text-[11px] text-slate-500">Dummy count shown on the storefront until real enrolments arrive.</div>
                </div>
                <div className="space-y-1">
                  <Label>Rating (display, 1&ndash;5)</Label>
                  <Input type="number" min="0" max="5" step="0.1" value={form.rating_placeholder}
                         onChange={(e) => set("rating_placeholder", parseFloat(e.target.value || "0"))}
                         data-testid="course-form-rating-placeholder" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label>Course badge (optional)</Label>
                  <Input value={form.course_badge}
                         onChange={(e) => set("course_badge", e.target.value)}
                         placeholder='e.g. "BESTSELLER" or "NEW"'
                         data-testid="course-form-badge" />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Pricing (placeholder)</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-4">
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3 sm:col-span-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Free course</div>
                    <div className="text-xs text-slate-500">Toggle off to set pricing.</div>
                  </div>
                  <Switch checked={form.is_free} onCheckedChange={(v) => set("is_free", v)} data-testid="course-form-free" />
                </div>
                <div className="space-y-1"><Label>Base price</Label><Input type="number" value={form.base_price} onChange={(e) => set("base_price", parseFloat(e.target.value || "0"))} disabled={form.is_free} /></div>
                <div className="space-y-1"><Label>Discount price</Label><Input type="number" value={form.discount_price || 0} onChange={(e) => set("discount_price", parseFloat(e.target.value || "0"))} disabled={form.is_free} /></div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3 sm:col-span-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">GST applicable</div>
                    <div className="text-[10px] text-slate-500">Show a GST line on the cart & Razorpay total.</div>
                  </div>
                  <Switch checked={form.gst_applicable} onCheckedChange={(v) => set("gst_applicable", v)} disabled={form.is_free} data-testid="course-form-gst-applicable" />
                </div>
                <div className="space-y-1">
                  <Label>GST %</Label>
                  <Input type="number" value={form.gst_percentage ?? 18}
                    onChange={(e) => set("gst_percentage", parseFloat(e.target.value || "0"))}
                    disabled={form.is_free || !form.gst_applicable}
                    placeholder="18" data-testid="course-form-gst-percentage" />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Price is tax-inclusive</div>
                    <div className="text-[10px] text-slate-500">Cart won&rsquo;t add GST on top; invoice will show reverse-calc GST.</div>
                  </div>
                  <Switch checked={!!form.tax_inclusive} onCheckedChange={(v) => set("tax_inclusive", v)} disabled={form.is_free || !form.gst_applicable} data-testid="course-form-tax-inclusive" />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3 sm:col-span-2">
                  <div className="text-sm font-semibold text-slate-900">Certificate applicable</div>
                  <Switch checked={form.certificate_applicable} onCheckedChange={(v) => set("certificate_applicable", v)} />
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">
                Trainer &amp; Certificate signature
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Shown on the right-hand signature block of every certificate issued for this course.
                Left signature (Managing Director) is fixed at the platform level.
              </p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Trainer name (on certificate)</Label>
                  <Input value={form.trainer_name || ""}
                    onChange={(e) => set("trainer_name", e.target.value)}
                    placeholder="e.g. Dr. Priya Nair"
                    data-testid="course-form-trainer-name" />
                </div>
                <div className="space-y-1">
                  <Label>Trainer designation</Label>
                  <Input value={form.trainer_designation || ""}
                    onChange={(e) => set("trainer_designation", e.target.value)}
                    placeholder="e.g. Senior Trainer · Infection Control"
                    data-testid="course-form-trainer-desig" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Trainer signature image</Label>
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
                    {form.trainer_signature_path ? (
                      <div className="flex items-center gap-3">
                        <div className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-mono text-slate-500">
                          {(form.trainer_signature_path || "").split("/").slice(-1)[0]}
                        </div>
                        <button type="button"
                          onClick={async () => {
                            if (!isEdit) return set("trainer_signature_path", "");
                            try {
                              await api.delete(`/admin/courses/${courseId}/trainer-signature`);
                              set("trainer_signature_path", "");
                              toast.success("Trainer signature removed.");
                            } catch (err) { toast.error(formatApiError(err)); }
                          }}
                          className="rounded border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                          data-testid="course-form-trainer-sig-remove">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">
                        No signature uploaded yet — the right block will just show trainer name.
                      </span>
                    )}
                    <label className="ml-auto inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      Upload signature
                      <input type="file" accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        data-testid="course-form-trainer-sig-input"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (!isEdit) {
                            toast.error("Save the course first, then upload the signature.");
                            e.target.value = ""; return;
                          }
                          const fd = new FormData();
                          fd.append("file", f);
                          try {
                            const { data } = await api.post(
                              `/admin/courses/${courseId}/trainer-signature`,
                              fd,
                              { headers: { "Content-Type": "multipart/form-data" } },
                            );
                            set("trainer_signature_path", data.data.trainer_signature_path);
                            toast.success("Trainer signature uploaded.");
                          } catch (err) { toast.error(formatApiError(err)); }
                          finally { e.target.value = ""; }
                        }} />
                    </label>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    PNG/JPG/WebP · max 2 MB · transparent PNG on white looks best.
                    Ideal ratio 4:1 (e.g. 600×150 px).
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#329112]">Publishing</h3>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
                  <div className="text-sm font-semibold text-slate-900">Featured</div>
                  <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
                  <div className="text-sm font-semibold text-slate-900">Mandatory-eligible</div>
                  <Switch checked={form.mandatory_eligible} onCheckedChange={(v) => set("mandatory_eligible", v)} />
                </div>
                {!isEdit && (
                  <div className="space-y-1"><Label>Version notes</Label><Input value={form.version_notes} onChange={(e) => set("version_notes", e.target.value)} placeholder="Initial version" /></div>
                )}
              </div>
              {!isEdit && (
                <div className="mt-3 text-xs text-slate-500">New courses start in <span className="font-semibold">Draft</span>. Publish from the course detail page after adding modules.</div>
              )}
            </section>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
              <button onClick={() => navigate(isEdit ? `/app/super-admin/courses/${courseId}` : "/app/super-admin/courses")} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="course-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit ? "Save changes" : "Create course"}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
